// Shared canvases carry the look plus whichever content the owner includes. Everything else
// is private: each viewer's screen fills it from their own main setup, and edits to it on a
// screen showing a shared canvas go to that viewer's own setup. Included OneDrive, Outlook,
// To Do and Planner content is fetched through the canvas with the owner's connection
// (store/screen.ts useSharedSource). Mirrors the server's rules
// (server/src/lib/canvasContent.ts), which strip private content whatever a client sends.

import type { Settings } from '../store/settings';
import { withDefaults } from '../store/settings';

export type ContentKey = 'calendars' | 'photos' | 'weather' | 'text' | 'news' | 'onedrive' | 'outlook' | 'todo' | 'planner' | 'powerbi';
export type Include = Record<ContentKey, boolean>;

export const DEFAULT_INCLUDE: Include = {
  calendars: false,
  photos: true,
  weather: true,
  text: true,
  news: true,
  onedrive: false,
  outlook: false,
  todo: false,
  planner: false,
  powerbi: false,
};

export const CONTENT_LABELS: Record<ContentKey, { name: string; warning?: string }> = {
  news: { name: 'News feeds' },
  weather: { name: 'Weather place' },
  text: { name: 'Text widget wording' },
  photos: { name: 'Photo and video web links' },
  calendars: { name: 'Calendar addresses', warning: 'Anyone it’s shared with will be able to read these calendars.' },
  onedrive: {
    name: 'OneDrive folders',
    warning: 'Everyone it’s shared with will see the photos and videos in your chosen folders (through your Microsoft connection).',
  },
  outlook: {
    name: 'Outlook calendars',
    warning: 'Everyone it’s shared with will see the events in your Outlook calendars, except the ones you switch off.',
  },
  todo: {
    name: 'To Do lists',
    warning: 'Everyone it’s shared with will see your To Do lists (except hidden ones); people who can edit it can add and tick tasks.',
  },
  planner: {
    name: 'Planner boards',
    warning: 'Everyone it’s shared with will see the tasks, dates and assignees on the Planner boards you chose for the widget.',
  },
  powerbi: {
    name: 'Power BI reports',
    warning:
      'People see your chosen reports with their own Power BI access. Without it, they see a report only if its workspace is on a Premium or Fabric capacity (then with a view-only link for that report).',
  },
};

type Path = readonly [section: keyof Settings, key: string];

const BY_CONTENT: Record<ContentKey, Path[]> = {
  calendars: [['calendar', 'sources']],
  photos: [['media', 'items']],
  weather: [['weather', 'location']],
  text: [
    ['text', 'heading'],
    ['text', 'body'],
  ],
  news: [['news', 'sources']],
  onedrive: [['media', 'cloudFolders']],
  outlook: [
    ['calendar', 'outlook'],
    ['calendar', 'outlookHidden'],
  ],
  todo: [
    ['lists', 'source'],
    ['lists', 'microsoftHidden'],
  ],
  planner: [['planner', 'plans']],
  powerbi: [['powerbi', 'items']],
};

/** Settings paths that stay with each viewer for a canvas with these options. */
export function privatePaths(include: Include): Path[] {
  return (Object.keys(BY_CONTENT) as ContentKey[]).filter((k) => !include[k]).flatMap((k) => BY_CONTENT[k]);
}

type Sections = Record<string, Record<string, unknown>>;
const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

function copyPaths(target: Settings, source: Settings, paths: Path[]): void {
  const t = target as unknown as Sections;
  const s = source as unknown as Sections;
  for (const [section, key] of paths) {
    t[section] = { ...t[section], [key]: clone(s[section]?.[key]) };
  }
}

/** What this viewer's screen shows: the canvas, with their own values for everything it doesn't share. */
export function mergeCanvas(canvas: Record<string, unknown>, main: Settings, include: Include): Settings {
  const out = withDefaults({ ...(canvas as Partial<Settings>), presets: main.presets });
  copyPaths(out, main, privatePaths(include));
  return out;
}

/** The part of this screen's setup that is saved to the canvas (private content left out). */
export function canvasPart(local: Settings, include: Include): Record<string, unknown> {
  const { presets: _p, ...rest } = clone(local);
  const s = rest as unknown as Sections;
  const neutral = withDefaults({}) as unknown as Sections;
  for (const [section, key] of privatePaths(include)) {
    s[section] = { ...s[section], [key]: clone(neutral[section]?.[key]) };
  }
  // Device files never leave this screen.
  const media = s.media as { items?: Array<{ local?: boolean }> };
  if (Array.isArray(media.items)) media.items = media.items.filter((i) => !i.local);
  return s;
}

/** The viewer's own main setup, updated with the private values edited on this screen. */
export function privateMerge(main: Settings, local: Settings, include: Include): Settings {
  const out = clone(main);
  copyPaths(out, local, privatePaths(include));
  out.presets = clone(local.presets);
  return out;
}
